import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import { useCreateKnowledgeBase } from '../hooks/useKnowledge';
import type { AssistantMissingKnowledgeBase } from '@/types/assistant';

/**
 * What the Assistant sidebar hands this page when the Composer guide asked for a
 * knowledge base the account does not have (task 1079). Router state, not the
 * URL, as with the connection pre-fill; every field stays editable.
 */
interface KnowledgeBasePrefillState {
  knowledgeBasePrefill?: AssistantMissingKnowledgeBase;
}

/**
 * Creating a knowledge base (task 574).
 *
 * A base is just a name and a description at birth — documents are added
 * afterwards from its detail page, because they already exist in the account
 * library and a base never owns them.
 */
export default function NewKnowledgeBasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const createKnowledgeBase = useCreateKnowledgeBase();
  const { knowledgeBasePrefill } = (location.state ?? {}) as KnowledgeBasePrefillState;
  const [name, setName] = useState(knowledgeBasePrefill?.name ?? '');
  const [description, setDescription] = useState(knowledgeBasePrefill?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const created = await createKnowledgeBase.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      navigate(`/knowledge-bases/${created.id}`);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Could not create the knowledge base. Please try again.'
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Create Knowledge Base"
        description="Name a grouping now; add documents to it from the account library once it exists."
      />
      <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="knowledge-base-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Name
            </label>
            <input
              id="knowledge-base-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Product Docs"
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label
              htmlFor="knowledge-base-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="knowledge-base-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          {error && (
            <p data-testid="knowledge-base-create-error" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/knowledge-bases')}
              disabled={createKnowledgeBase.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createKnowledgeBase.isPending || name.trim().length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createKnowledgeBase.isPending ? 'Creating...' : 'Create knowledge base'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
